import { useState, useEffect, useCallback, useRef, useMemo, startTransition } from "react";
import { X, ChevronLeft, ChevronRight, LayoutGrid, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import {
  motion,
  animate,
  useMotionValue,
  useReducedMotion,
  type PanInfo,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { CarImagePlaceholder } from "@/components/cars/CarImagePlaceholder";
import { carImageAttributes, carImageAspectRatio } from "@/lib/car-image-attributes";

interface ImageData {
  intrinsic_dimensions?: Record<string, { width: number; height: number }>;
  id: string;
  url: string;
  r2_path: string | null;
  display_order: number;
  cdn_thumbnail?: string | null;
  cdn_medium?: string | null;
  /** Task #7090: explicit existence signal for the 1280w variant — populated
   *  by the API only when the `_1280w.webp` object is verified to exist. */
  cdn_large?: string | null;
  cdn_full?: string | null;
}

interface VideoData {
  id: number;
  stream_uid: string;
  status: string;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  playback_url: string | null;
  display_order: number;
}

interface PhotoGalleryProps {
  images: ImageData[];
  carTitle: string;
  carId?: number;
  /**
   * Cloudflare Stream videos for this listing (task: video listings v1).
   * Videos are interleaved into the gallery matrix as thumbnail tiles with
   * a play badge and play inside the lightbox when clicked. Only rows that
   * are `ready` with both a `playback_url` and a `thumbnail_url` are shown —
   * everything else is skipped so we never render a broken tile.
   */
  videos?: VideoData[];
}

/**
 * One ordered entry in the unified gallery. Images and ready videos share a
 * single ordered collection so the grid, the mobile hero and the lightbox all
 * iterate over the same indices.
 */
type MediaItem =
  | { kind: "image"; key: string; image: ImageData; order: number }
  | { kind: "video"; key: string; video: VideoData; order: number };

function getImageUrl(img: ImageData, size: "thumbnail" | "medium" | "full" = "full") {
  if (size === "thumbnail" && img.cdn_thumbnail) return img.cdn_thumbnail;
  if (size === "medium" && img.cdn_medium) return img.cdn_medium;
  if (size === "full" && img.cdn_full) return img.cdn_full;
  return img.url;
}

// Desktop/grid gallery srcset. Only widths GUARANTEED to exist are advertised
// (400w/800w/2400w from the real cdn_* fields). A synthesized 1280w candidate
// (Task #5922) was removed: it was string-derived from cdn_full and 404'd for the
// subset of the fleet whose `_1280w.webp` object was never generated, breaking the
// hero on viewports/DPRs that select ~1280w. Task #7090 restored the tier via the
// explicit `cdn_large` existence field (never string synthesis). See lib/cdn.ts.
// Exported for the server/client hero-preload parity test (task #7080) —
// scripts/serve-static.mjs buildHeroPreloadSrcset must stay byte-identical.
export function getSrcSet(img: ImageData): string | undefined {
  const entries: string[] = [];
  if (img.cdn_thumbnail) entries.push(`${img.cdn_thumbnail} 400w`);
  if (img.cdn_medium) entries.push(`${img.cdn_medium} 800w`);
  if (img.cdn_large) entries.push(`${img.cdn_large} 1280w`);
  if (img.cdn_full) entries.push(`${img.cdn_full} 2400w`);
  return entries.length > 0 ? entries.join(", ") : undefined;
}

// LCP (Task #5918): the full-bleed mobile hero renders at 100vw. The mobile
// srcset caps at 800w and excludes the oversized 2400w full image (~300-500KB)
// so high-DPR phones don't download it as their LCP element. A 1280w tier
// (Task #5922) was removed here because the synthesized `_1280w.webp` URL 404s
// for cars whose 1280w object was never generated — with 2400w already excluded
// on mobile, that left those phones with NO valid large candidate and a BROKEN
// hero (the reported mobile-only bug). Task #7090 restored the tier via the
// explicit `cdn_large` existence field (backfill-verified), not string synthesis:
// high-DPR phones get a crisp ~1280w hero while still never downloading 2400w.
// Exported for the server/client hero-preload parity test (task #7080) —
// scripts/serve-static.mjs buildMobileHeroPreloadSrcset must stay byte-identical.
export function getMobileHeroSrcSet(img: ImageData): string | undefined {
  const entries: string[] = [];
  if (img.cdn_thumbnail) entries.push(`${img.cdn_thumbnail} 400w`);
  if (img.cdn_medium) entries.push(`${img.cdn_medium} 800w`);
  if (img.cdn_large) entries.push(`${img.cdn_large} 1280w`);
  return entries.length > 0 ? entries.join(", ") : undefined;
}

// Desktop hero-tile `sizes` per gallery layout (task #7090). The multi-tile
// layouts cap the width hint so DPR-1 desktops never over-download:
//   - 1280px cap when the image carries a verified 1280w tier (`cdn_large`) —
//     big high-res desktop screens select the crisp 1280w candidate instead of
//     being pinned to 800w (or over-downloading the ~2400w full image).
//   - 800px cap otherwise (task #7080): with only 800w/2400w real candidates,
//     an uncapped vw hint makes common desktop widths download 2400w.
// The single-photo layout stays 100vw (true full-bleed).
// MUST stay byte-identical to CarDetailPage.buildHeroDesktopSizes and
// serve-static.mjs buildHeroPreloadDesktopSizes so the preloaded candidate ==
// painted candidate. Exported for the hero-preload parity test.
export function getHeroTileSizes(
  count: number,
  img?: Pick<ImageData, "cdn_large"> | null,
): string {
  const cap = img?.cdn_large ? "1280px" : "800px";
  const desktop =
    count <= 1
      ? "100vw"
      : count === 2
        ? `min(50vw, ${cap})`
        : count === 3
          ? `min(66vw, ${cap})`
          : `min(60vw, ${cap})`;
  return count <= 1 ? desktop : `(max-width: 640px) 100vw, ${desktop}`;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Real gesture physics (task #7900)
 *
 * The strip follows the finger 1:1 during a drag (framer-motion `drag="x"`,
 * which also sets `touch-action: pan-y` semantics so vertical page scroll
 * still works — we set it explicitly too). On release the position is
 * projected from the release velocity; the slide nearest the projection
 * wins (clamped to the adjacent slide, like native pagers), and the spring
 * is seeded with the release velocity so momentum carries through. Springs
 * are interruptible: a new pointer-down stops the in-flight animation and
 * the drag takes over from the live position. Real bounds + `dragElastic`
 * give rubber-band resistance past the first/last slide instead of the old
 * modulo wrap. Reduced-motion users get instant position updates.
 * ────────────────────────────────────────────────────────────────────────── */

export const SWIPE_SPRING = { type: "spring" as const, stiffness: 340, damping: 34, mass: 0.8 };
/** Momentum projection horizon (seconds): projected = x + v * horizon. */
const PROJECTION_S = 0.18;
/** Drags shorter than this (px) are treated as taps — the click goes through. */
export const TAP_SLOP_PX = 10;

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Pure decision function for swipe-commit: given the strip's current x
 * position, release velocity, slide width, active index, and total slide
 * count, returns the index the strip should snap to.
 *
 * The strip's position is projected forward by `velocityX * PROJECTION_S`
 * (momentum horizon), then rounded to the nearest slide and clamped to the
 * adjacent slide (at most ±1 from `index`, never out of [0, count-1]).
 *
 * Exported so regression tests can exercise this math directly without
 * mounting a component or faking framer-motion.
 */
export function resolveSwipeTarget({
  xPos,
  velocityX,
  width,
  index,
  count,
}: {
  /** Current x position of the strip (negative for slides after the first). */
  xPos: number;
  /** Horizontal release velocity from PanInfo (px/s). */
  velocityX: number;
  /** Pixel width of one slide. */
  width: number;
  /** Currently active slide index (0-based). */
  index: number;
  /** Total number of slides. */
  count: number;
}): number {
  const projected = xPos + velocityX * PROJECTION_S;
  return clamp(
    Math.round(-projected / width),
    Math.max(0, index - 1),
    Math.min(count - 1, index + 1),
  );
}

/** Measured width of an element, kept fresh via ResizeObserver. */
function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * Shared gesture state for a horizontally swipeable strip of `count` slides,
 * each `width`px wide. Returns the strip's motion value, the drag props to
 * spread onto the `motion.div`, and a capture-phase click guard that
 * swallows the synthetic click fired right after a real drag (so a drag
 * never opens the lightbox / closes the dialog).
 */
function useSwipeStrip({
  count,
  index,
  setIndex,
  width,
  enabled,
}: {
  count: number;
  index: number;
  setIndex: (i: number) => void;
  width: number;
  enabled: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const draggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  /** Release velocity handed from onDragEnd to the index-change spring. */
  const releaseVelocityRef = useRef(0);

  // Keep the strip aligned to the active slide whenever index or width
  // changes (drag commit, arrows, thumbnails, keyboard, resize).
  useEffect(() => {
    if (width <= 0 || draggingRef.current) return;
    const target = -index * width;
    if (reducedMotion) {
      x.set(target);
      return;
    }
    const velocity = releaseVelocityRef.current;
    releaseVelocityRef.current = 0;
    const controls = animate(x, target, { ...SWIPE_SPRING, velocity });
    return () => controls.stop();
  }, [index, width, reducedMotion, x]);

  const onDragStart = useCallback(() => {
    draggingRef.current = true;
  }, []);

  const onDragEnd = useCallback(
    (_event: unknown, info: PanInfo) => {
      draggingRef.current = false;
      if (Math.abs(info.offset.x) > TAP_SLOP_PX) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 300);
      }
      if (width <= 0) return;
      // Momentum projection: where would the strip settle if the release
      // velocity decayed? Snap to the slide nearest that projection,
      // clamped to the adjacent slide (native-pager feel). A slow small
      // drag projects near the current slide and snaps back.
      const target = resolveSwipeTarget({
        xPos: x.get(),
        velocityX: info.velocity.x,
        width,
        index,
        count,
      });
      if (target === index) {
        if (reducedMotion) {
          x.set(-index * width);
        } else {
          animate(x, -index * width, { ...SWIPE_SPRING, velocity: info.velocity.x });
        }
      } else {
        releaseVelocityRef.current = info.velocity.x;
        setIndex(target);
      }
    },
    [count, index, setIndex, width, x, reducedMotion],
  );

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
    }
  }, []);

  const dragProps =
    enabled && width > 0
      ? {
          drag: "x" as const,
          dragConstraints: { left: -(count - 1) * width, right: 0 },
          dragElastic: 0.28,
          dragMomentum: false,
          onDragStart,
          onDragEnd,
        }
      : {};

  return { x, dragProps, onClickCapture };
}

/** Build the Cloudflare Stream embed URL for the lightbox, muted-autoplaying
 *  so the clip starts immediately (browsers allow muted autoplay; the player
 *  exposes its own unmute control). */
function videoEmbedUrl(url: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}autoplay=true&muted=true`;
}

function PlayBadge({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const dims = size === "lg" ? "h-16 w-16" : size === "sm" ? "h-7 w-7" : "h-12 w-12";
  const icon = size === "lg" ? "h-7 w-7" : size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5";
  return (
    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span className={cn("flex items-center justify-center rounded-full material-chip-dark text-white", dims)}>
        <Play className={cn("translate-x-px", icon)} fill="currentColor" />
      </span>
    </span>
  );
}

/** Thumbnail strip tile for image items in the lightbox. Tracks its own
 *  broken-image state so a 404'd thumbnail shows the neutral placeholder
 *  instead of the browser's broken-image icon, without affecting other tiles
 *  or the main slide. */
function ThumbnailImage({ item, alt }: { item: MediaItem & { kind: "image" }; alt: string }) {
  const { t } = useTranslation();
  const [errored, setErrored] = useState(false);
  if (errored) {
    return <CarImagePlaceholder className="h-full w-full" label={t("common.no_image")} />;
  }
  return (
    <img
      src={getImageUrl(item.image, "thumbnail")}
      {...carImageAttributes(getImageUrl(item.image, "thumbnail"), item.image.intrinsic_dimensions)}
      alt={alt}
      className="h-full w-full object-cover"
      onError={() => setErrored(true)}
    />
  );
}

/** One lightbox slide with its own broken-image state, so a dead photo only
 *  shows the placeholder for the slide that actually failed. Videos render
 *  their autoplaying iframe only while active — inactive neighbours show the
 *  thumbnail + play badge so preloaded slides never start playback. */
function LightboxSlide({
  item,
  active,
  carTitle,
  index,
}: {
  item: MediaItem;
  active: boolean;
  carTitle: string;
  index: number;
}) {
  const { t } = useTranslation();
  const [errored, setErrored] = useState(false);
  if (item.kind === "video") {
    if (active && item.video.playback_url) {
      return (
        <div
          className="flex h-[85vh] w-[90vw] max-w-5xl items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            src={videoEmbedUrl(item.video.playback_url)}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
            className="aspect-video h-auto max-h-full w-full rounded-xl bg-black"
            title={t("photoGallery.video_title", { title: carTitle })}
          />
        </div>
      );
    }
    return (
      <div
        className="relative flex max-h-[85vh] max-w-[90vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={item.video.thumbnail_url ?? ""}
          alt={t("photoGallery.video_thumbnail_alt", { title: carTitle, index: index + 1 })}
          className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain"
          draggable={false}
        />
        <PlayBadge size="lg" />
      </div>
    );
  }
  if (errored) {
    return (
      <div
        className="flex h-[60vh] w-[90vw] max-w-3xl items-center justify-center rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <CarImagePlaceholder label={t("common.no_image")} className="rounded-xl" />
      </div>
    );
  }
  return (
    <img
      src={getImageUrl(item.image, "full")}
      {...carImageAttributes(getImageUrl(item.image, "full"), item.image.intrinsic_dimensions)}
      alt={`${carTitle} ${index + 1}`}
      className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain select-none"
      onClick={(e) => e.stopPropagation()}
      onError={() => setErrored(true)}
      draggable={false}
    />
  );
}

/** Renders the visual contents of a gallery tile — an `<img>` for photos, or a
 *  thumbnail + play badge for videos. Wrapping element must be positioned
 *  (`relative`) so the badge centers over the tile. */
export function MediaContent({
  item,
  alt,
  className,
  style,
  sizes,
  loading,
  draggable,
  badgeSize,
  fetchPriority,
  capFullVariant,
}: {
  item: MediaItem;
  alt: string;
  className: string;
  style?: React.CSSProperties;
  sizes?: string;
  loading?: "lazy" | "eager";
  draggable?: boolean;
  badgeSize?: "sm" | "md" | "lg";
  /** CWV/LCP (task #4410): set "high" on the first/hero photo so the car
   *  detail page's LCP element is prioritised. The gallery hero is a
   *  responsive <img> (srcset+sizes), so fetchpriority — not a plain href
   *  preload — is the correct lever (a fixed-href preload would mismatch
   *  the candidate the browser actually picks and double-fetch). */
  fetchPriority?: "high";
  /** LCP (task #5918): drop the oversized 2400w candidate from the srcset.
   *  Set on the full-bleed mobile hero so high-DPR phones cap at the 800w
   *  variant instead of downloading the full image at 100vw. */
  capFullVariant?: boolean;
}) {
  const { t } = useTranslation();
  // Legacy listings whose only photo origin is a dead 404 (and oversized /
  // undecodable originals the variant backfill could not re-host) would render
  // the browser's broken-image icon here. Swap to a neutral placeholder tile
  // that fills the same slot instead.
  const source = item.kind === "image"
    ? getImageUrl(item.image, "medium")
    : item.video.thumbnail_url ?? "";
  // Track the source rather than a one-way boolean: replacing/retrying a photo
  // must not inherit another source's loaded or broken state.
  const [loadedSource, setLoadedSource] = useState<string | null>(null);
  const [erroredSource, setErroredSource] = useState<string | null>(null);
  const errored = !source || erroredSource === source;
  const pending = !errored && loadedSource !== source;
  const imageRef = useCallback((node: HTMLImageElement | null) => {
    // SSR/preloaded images can finish before React attaches onLoad.
    if (!node?.complete || !node.currentSrc) return;
    if (node.naturalWidth > 0) setLoadedSource(source);
    else setErroredSource(source);
  }, [source]);
  const placeholder = pending ? (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true" data-testid="gallery-media-loading">
      <div className="bac-shimmer h-full w-full" />
    </div>
  ) : null;
  if (item.kind === "image") {
    if (errored) {
      return <CarImagePlaceholder className={className} label={t("common.no_image")} />;
    }
    return (
      <>
      <img
        key={source}
        ref={imageRef}
        src={source}
        {...carImageAttributes(getImageUrl(item.image, "medium"), item.image.intrinsic_dimensions)}
        srcSet={capFullVariant ? getMobileHeroSrcSet(item.image) : getSrcSet(item.image)}
        alt={alt}
        className={className}
        style={{ ...style, aspectRatio: carImageAspectRatio(item.image.intrinsic_dimensions) || "4 / 3" }}
        sizes={sizes}
        loading={loading}
        draggable={draggable}
        aria-busy={pending}
        onLoad={() => setLoadedSource(source)}
        onError={() => setErroredSource(source)}
        {...(fetchPriority ? { fetchPriority } : {})}
      />
      {placeholder}
      </>
    );
  }
  return (
    <>
      <img
        key={source}
        ref={imageRef}
        src={source}
        alt={alt}
        className={className}
        style={style}
        loading={loading}
        draggable={draggable}
        aria-busy={pending}
        onLoad={() => setLoadedSource(source)}
        onError={() => setErroredSource(source)}
        {...(fetchPriority ? { fetchPriority } : {})}
      />
      {placeholder}
      <PlayBadge size={badgeSize} />
    </>
  );
}

export function PhotoGallery({ images, carTitle, carId, videos }: PhotoGalleryProps) {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Unified, ordered media: photos + ready videos interleaved by
  // display_order (images arrive from the API already ordered by
  // display_order ASC, id ASC — the first image is the hero; the retired
  // `is_primary` flag is no longer shipped). Sort stability preserves the
  // API order across ties so the host's intended order holds in the grid,
  // the mobile hero and the lightbox.
  const media = useMemo<MediaItem[]>(() => {
    const imageItems: MediaItem[] = images.map((img) => ({
      kind: "image",
      key: `img-${img.id}`,
      image: img,
      order: img.display_order,
    }));
    const videoItems: MediaItem[] = (videos ?? [])
      .filter((v) => v.status === "ready" && !!v.playback_url && !!v.thumbnail_url)
      .map((v) => ({
        kind: "video",
        key: `vid-${v.id}`,
        video: v,
        order: v.display_order,
      }));
    return [...imageItems, ...videoItems].sort((a, b) => a.order - b.order);
  }, [images, videos]);

  // Guard: clamp currentIndex whenever the media array shrinks (e.g., a host
  // deletes a photo while the lightbox is open, or the component re-renders
  // with a shorter images prop). Without this, currentIndex can point past the
  // end of the new array — leaving a blank slide or crashing LightboxSlide.
  // When media becomes completely empty, close the lightbox too, since there is
  // no valid slide to show.
  useEffect(() => {
    if (media.length === 0) {
      setLightboxOpen(false);
      setCurrentIndex(0);
    } else {
      setCurrentIndex((i) => (i >= media.length ? media.length - 1 : i));
    }
  }, [media.length]);

  const openLightbox = (index: number) => {
    void import("@/lib/analytics").then(({ track }) => {
      track("car_photos_opened", { car_id: carId ?? 0, photo_count: media.length });
    });
    // INP: opening the Dialog mounts a Radix portal + focus trap — wrap in
    // startTransition so React yields to the browser before committing, keeping
    // the tap response fast (the lightbox just shows a moment later).
    startTransition(() => {
      setCurrentIndex(index);
      setLightboxOpen(true);
    });
  };

  const closeLightbox = () => setLightboxOpen(false);

  // Bounded pager (task #7900): arrows/keyboard clamp at the ends instead of
  // the old modulo wrap, matching the drag physics (real bounds + rubber-band)
  // and the sparse lightbox strip, which only mounts the active slide ±1 — a
  // wrap jump would animate across unmounted, blank slides.
  const goNext = useCallback(() => {
    setCurrentIndex(i => Math.min(i + 1, media.length - 1));
  }, [media.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex(i => Math.max(i - 1, 0));
  }, [media.length]);

  // Gesture state for the two swipeable strips (inline mobile hero and the
  // lightbox). Each gets its own motion value/width but shares currentIndex.
  const [heroRef, heroWidth] = useElementWidth<HTMLDivElement>();
  const hero = useSwipeStrip({
    count: media.length,
    index: currentIndex,
    setIndex: setCurrentIndex,
    width: heroWidth,
    enabled: media.length > 1,
  });

  // Lightbox slides are viewport-wide; track window width while open.
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0,
  );
  useEffect(() => {
    if (!lightboxOpen) return;
    const measure = () => setViewportWidth(window.innerWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [lightboxOpen]);
  const lightbox = useSwipeStrip({
    count: media.length,
    index: currentIndex,
    setIndex: setCurrentIndex,
    width: lightboxOpen ? viewportWidth : 0,
    enabled: lightboxOpen && media.length > 1,
  });

  // Arrow-key navigation only. ESC, focus-trapping and focus-restore are
  // handled by the Radix Dialog wrapping the lightbox below.
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, goNext, goPrev]);

  if (media.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center bg-muted text-muted-foreground sm:h-96">
        {t("photoGallery.no_photos")}
      </div>
    );
  }

  const count = media.length;

  return (
    <>
      {/* Mobile-only: edge-to-edge swipeable hero with index counter.
          The strip tracks the finger 1:1 (framer drag), rubber-bands past
          the ends, and commits by momentum projection on release. */}
      <div ref={heroRef} className="relative w-full overflow-hidden bg-black md:hidden">
        <motion.div
          className="flex aspect-[4/3] w-full"
          style={{ x: hero.x, touchAction: "pan-y" }}
          onClickCapture={hero.onClickCapture}
          {...hero.dragProps}
        >
          {media.map((item, i) => (
            <button
              key={item.key}
              type="button"
              onClick={() => openLightbox(i)}
              className="relative h-full w-full shrink-0"
              aria-label={
                item.kind === "video"
                  ? t("photoGallery.play_video_aria", { index: i + 1, count })
                  : t("photoGallery.open_photo_aria", { index: i + 1, count })
              }
            >
              <MediaContent
                item={item}
                alt={`${carTitle} ${i + 1}`}
                className="h-full w-full object-cover"
                sizes="100vw"
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
                badgeSize="lg"
                capFullVariant
                {...(i === 0 ? { fetchPriority: "high" as const } : {})}
              />
            </button>
          ))}
        </motion.div>
        {count > 1 && (
          <div
            data-testid="gallery-counter"
            className="pointer-events-none absolute bottom-3 right-3 rounded-full material-chip-dark px-2.5 py-1 text-xs font-medium text-white"
          >
            {currentIndex + 1} / {count}
          </div>
        )}
        {count > 1 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-1.5 flex justify-center gap-1">
            {media.slice(0, Math.min(count, 8)).map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-[width]",
                  i === Math.min(currentIndex, 7) ? "w-4 bg-white" : "w-1.5 bg-white/60",
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Desktop / tablet grid */}
      <div className="hidden md:block">
      {count === 1 && (
        <div className="relative overflow-hidden rounded-2xl" style={{ height: "560px" }}>
          <div className="relative h-full cursor-pointer" onClick={() => openLightbox(0)}>
            <MediaContent
              item={media[0]}
              alt={carTitle}
              className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              sizes="100vw"
              style={{ minHeight: "300px", maxHeight: "560px" }}
              badgeSize="lg"
              fetchPriority="high"
            />
          </div>
        </div>
      )}

      {count === 2 && (
        <div className="relative grid grid-cols-1 gap-2 overflow-hidden rounded-xl sm:grid-cols-2" style={{ height: "600px" }}>
          {media.map((item, i) => (
            <div key={item.key} className="relative cursor-pointer overflow-hidden" onClick={() => openLightbox(i)}>
              <MediaContent
                item={item}
                alt={`${carTitle} ${i + 1}`}
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                sizes={getHeroTileSizes(2, item.kind === "image" ? item.image : null)}
                style={{ minHeight: "300px", maxHeight: "600px" }}
                loading="eager"
                {...(i === 0 ? { fetchPriority: "high" as const } : {})}
              />
            </div>
          ))}
        </div>
      )}

      {count === 3 && (
        <div className="relative grid grid-cols-1 gap-2 overflow-hidden rounded-xl sm:grid-cols-3 sm:grid-rows-2" style={{ height: "600px" }}>
          <div className="relative sm:col-span-2 sm:row-span-2 cursor-pointer overflow-hidden" onClick={() => openLightbox(0)}>
            <MediaContent
              item={media[0]}
              alt={carTitle}
              className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              sizes={getHeroTileSizes(3, media[0].kind === "image" ? media[0].image : null)}
              style={{ minHeight: "300px", maxHeight: "600px" }}
              badgeSize="lg"
            />
          </div>
          {media.slice(1, 3).map((item, i) => (
            <div key={item.key} className="relative hidden sm:block cursor-pointer overflow-hidden" onClick={() => openLightbox(i + 1)}>
              <MediaContent
                item={item}
                alt={`${carTitle} ${i + 2}`}
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                sizes="33vw"
                loading="eager"
              />
            </div>
          ))}
        </div>
      )}

      {count === 4 && (
        <div className="relative grid grid-cols-1 gap-2 overflow-hidden rounded-xl sm:grid-cols-5 sm:grid-rows-2" style={{ height: "600px" }}>
          <div className="relative sm:col-span-3 sm:row-span-2 cursor-pointer overflow-hidden" onClick={() => openLightbox(0)}>
            <MediaContent
              item={media[0]}
              alt={carTitle}
              className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              sizes={getHeroTileSizes(4, media[0].kind === "image" ? media[0].image : null)}
              style={{ minHeight: "300px", maxHeight: "600px" }}
              badgeSize="lg"
            />
          </div>
          {media.slice(1, 4).map((item, i) => (
            <div key={item.key} className={cn("relative hidden cursor-pointer overflow-hidden sm:block", i === 0 ? "sm:col-span-2" : "")} onClick={() => openLightbox(i + 1)}>
              <MediaContent
                item={item}
                alt={`${carTitle} ${i + 2}`}
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                sizes={i === 0 ? "40vw" : "20vw"}
                loading="eager"
              />
            </div>
          ))}
        </div>
      )}

      {count >= 5 && (
        <div className="relative grid grid-cols-1 gap-2 overflow-hidden rounded-xl sm:grid-cols-5 sm:grid-rows-2" style={{ height: "600px" }}>
          <div className="relative sm:col-span-3 sm:row-span-2 cursor-pointer overflow-hidden" onClick={() => openLightbox(0)}>
            <MediaContent
              item={media[0]}
              alt={carTitle}
              className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              sizes={getHeroTileSizes(5, media[0].kind === "image" ? media[0].image : null)}
              style={{ minHeight: "300px", maxHeight: "600px" }}
              badgeSize="lg"
            />
          </div>
          {media.slice(1, 5).map((item, i) => (
            <div key={item.key} className="relative hidden cursor-pointer overflow-hidden sm:block" onClick={() => openLightbox(i + 1)}>
              <MediaContent
                item={item}
                alt={`${carTitle} ${i + 2}`}
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                sizes="20vw"
                loading="eager"
              />
            </div>
          ))}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/35 to-transparent" />
          <button
            onClick={() => openLightbox(0)}
            className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full material-chip px-5 py-2.5 text-sm font-semibold text-foreground shadow-lg ring-1 ring-black/5 transition-[transform,background-color] hover:scale-[1.03] hover:bg-background"
          >
            <LayoutGrid className="h-[18px] w-[18px]" />
            {t("photoGallery.show_all_photos", { count })}
          </button>
        </div>
      )}
      </div>

      <noscript>
        <section className="mt-4">
          <h2 className="mb-3 text-lg font-semibold">
            {t("photoGallery.lightbox_title", { defaultValue: "{{title}} photo gallery", title: carTitle })}
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {images.filter(image => !!(image.cdn_full || image.url)).map((image, index) => (
              <a key={image.id || image.url} href={image.cdn_full || image.url}>
                <img src={image.cdn_full || image.url} loading="lazy"
                  {...carImageAttributes(image.cdn_full || image.url, image.intrinsic_dimensions)}
                  className="aspect-[4/3] w-full rounded-lg object-cover"
                  alt={t("photoGallery.thumbnail_alt", { title: carTitle, index: index + 1 })} />
              </a>
            ))}
          </div>
        </section>
      </noscript>
      <Dialog.Root open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/95" />
          <Dialog.Content className="fixed inset-0 z-50 overflow-hidden bg-transparent outline-none">
          <Dialog.Title className="sr-only">
            {t("photoGallery.lightbox_title", { defaultValue: "{{title}} photo gallery", title: carTitle })}
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="cursor-pointer absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20"
              aria-label={t("photoGallery.close")}
            >
              <X className="h-5 w-5" />
            </button>
          </Dialog.Close>

          {count > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              disabled={currentIndex === 0}
              className="absolute left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20 disabled:opacity-30 disabled:hover:bg-background/10"
              aria-label={t("photoGallery.previous")}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {/* Viewport-wide swipeable strip. Only the active slide and its
              immediate neighbours render (so we never eager-load every full
              image, and offscreen videos never mount their autoplaying
              iframe). Clicks on the slide backdrop close the lightbox; a
              real drag's synthetic click is swallowed by onClickCapture. */}
          <motion.div
            className="absolute inset-0"
            style={{ x: lightbox.x, touchAction: "pan-y" }}
            onClickCapture={lightbox.onClickCapture}
            {...lightbox.dragProps}
          >
            {media.map((item, i) =>
              Math.abs(i - currentIndex) <= 1 ? (
                <div
                  key={item.key}
                  className="absolute inset-y-0 flex items-center justify-center"
                  style={{ left: i * viewportWidth, width: viewportWidth }}
                  onClick={closeLightbox}
                >
                  <LightboxSlide item={item} active={i === currentIndex} carTitle={carTitle} index={i} />
                </div>
              ) : null,
            )}
          </motion.div>

          {count > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                disabled={currentIndex === count - 1}
                className="absolute right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/10 text-white transition-colors hover:bg-background/20 disabled:opacity-30 disabled:hover:bg-background/10"
                aria-label={t("photoGallery.next")}
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/60">
                {currentIndex + 1} / {count}
              </div>
            </>
          )}

          {count > 1 && (
            <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-1.5 max-w-[80vw] overflow-x-auto px-2 py-1">
              {media.map((item, i) => (
                <button
                  key={item.key}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                  className={cn(
                    "h-12 w-16 shrink-0 overflow-hidden rounded-md border-2 transition-[border-color,opacity]",
                    i === currentIndex ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-75"
                  )}
                >
                  {item.kind === "image" ? (
                    <ThumbnailImage item={item} alt={t("photoGallery.thumbnail_alt", { title: carTitle, index: i + 1 })} />
                  ) : (
                    <span className="relative block h-full w-full">
                      <img src={item.video.thumbnail_url ?? ""} alt={t("photoGallery.video_thumbnail_alt", { title: carTitle, index: i + 1 })} className="h-full w-full object-cover" />
                      <PlayBadge size="sm" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
